/**
 * 旧 CRM (S/N情報 Excel) からの稼働機器一括取込ダイアログ。
 *
 * フロー:
 *  1. ファイル選択 (xlsx)
 *  2. パース → プレビュー
 *     - 取込列のチェックボックス (デフォルト全 ON)
 *     - 種別ごとの件数サマリー
 *     - 未マッチ顧客一覧 (各購入者ごとに「スキップ / 新規作成」を選択)
 *  3. 実行 → bulkImportSoldDevices Callable
 *
 * 種別ごとの分岐:
 * - 'NES Health': シリアル番号 (X列) + nesHealth.{Mind/MR/SMI/機器種類/BWSメール/出荷/入荷}
 * - それ以外 ('TimeWaver本体' / 'TimeWaverオプション' / 'Bio-Well' / 'Theragem'):
 *   シリアルナンバー (Q列) + 保証日 + サービスタグ等
 */
import { FileSpreadsheet, Upload } from 'lucide-react';
import Papa from 'papaparse';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useMailContacts, type MailContactRecord } from '@/hooks/useMailContacts';
import {
  useBulkImportSoldDevices,
  type BulkImportSoldDeviceItem,
} from '@/hooks/useSalesOps';
import { db } from '@/lib/firebase';
import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Excel 列インデックス (0-based) */
const COL = {
  legacyDataId: 0, // A データID
  createdAt: 8, // I 作成日時
  purchaserName: 15, // P 購入者
  twSerial: 16, // Q シリアルナンバー (TimeWaver系)
  nesDeviceKind: 18, // S NES Health_機器種類
  shippedAt: 19, // T 出荷
  receivedAt: 20, // U 入荷
  mind: 21, // V Mind
  productKind: 22, // W 種別
  nesSerial: 23, // X シリアル番号 (E4L)
  bwsEmail: 24, // Y BWSアカウント用アドレス
  warrantyStart: 25, // Z 保証開始日
  warrantyEnd: 26, // AA 保証終了日
  productName: 27, // AB 商品
  mr: 28, // AC MR
  smi: 29, // AD SMI
  serviceTag: 31, // AF サービスタグ
  expressCode: 32, // AG エクスプレスコード
} as const;

/** 取込対象フィールドのトグル ID */
type FieldKey =
  | 'serialNumber'
  | 'warranty'
  | 'serviceTag'
  | 'expressCode'
  | 'nesMind'
  | 'nesMr'
  | 'nesSmi'
  | 'nesDeviceKind'
  | 'nesBwsEmail'
  | 'nesShipping';

const FIELD_LABELS: Record<FieldKey, { label: string; scope: 'common' | 'nesOnly' }> = {
  serialNumber: { label: 'シリアル番号 (種別に応じて Q列/X列を自動選択)', scope: 'common' },
  warranty: { label: '保証開始日 / 保証終了日', scope: 'common' },
  serviceTag: { label: 'サービスタグ', scope: 'common' },
  expressCode: { label: 'エクスプレスコード', scope: 'common' },
  nesMind: { label: 'Mind', scope: 'nesOnly' },
  nesMr: { label: 'MR', scope: 'nesOnly' },
  nesSmi: { label: 'SMI', scope: 'nesOnly' },
  nesDeviceKind: { label: 'NES Health_機器種類', scope: 'nesOnly' },
  nesBwsEmail: { label: 'BWSアカウント用アドレス', scope: 'nesOnly' },
  nesShipping: { label: '出荷日 / 入荷日', scope: 'nesOnly' },
};

interface ParsedRow {
  rowIndex: number;
  legacyDataId: string;
  purchaserName: string;
  productKind: string;
  productName: string;
  createdAt: string;
  twSerial: string;
  nesSerial: string;
  warrantyStart: string;
  warrantyEnd: string;
  serviceTag: string;
  expressCode: string;
  nesDeviceKind: string;
  mind: boolean | null;
  mr: boolean | null;
  smi: boolean | null;
  bwsEmail: string;
  shippedAt: string;
  receivedAt: string;
  /** 解決済み contactId (null = 未マッチ) */
  contactId: string | null;
}

type UnmatchedAction = 'skip' | 'create';

function s(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number') return String(v);
  if (v instanceof Date) return v.toISOString();
  return String(v).trim();
}

function parseBoolFlag(v: unknown): boolean | null {
  const t = s(v).toLowerCase();
  if (!t) return null;
  if (t === 'true' || t === '1' || t === 'はい') return true;
  if (t === 'false' || t === '0' || t === 'いいえ') return false;
  return null;
}

function parseSheet(rows: unknown[][]): ParsedRow[] {
  const out: ParsedRow[] = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    if (!r) continue;
    const legacyDataId = s(r[COL.legacyDataId]);
    if (!legacyDataId) continue;
    const productKind = s(r[COL.productKind]);
    if (!productKind) continue;
    out.push({
      rowIndex: i + 1,
      legacyDataId,
      purchaserName: s(r[COL.purchaserName]),
      productKind,
      productName: s(r[COL.productName]),
      createdAt: s(r[COL.createdAt]),
      twSerial: s(r[COL.twSerial]),
      nesSerial: s(r[COL.nesSerial]),
      warrantyStart: s(r[COL.warrantyStart]),
      warrantyEnd: s(r[COL.warrantyEnd]),
      serviceTag: s(r[COL.serviceTag]),
      expressCode: s(r[COL.expressCode]),
      nesDeviceKind: s(r[COL.nesDeviceKind]),
      mind: parseBoolFlag(r[COL.mind]),
      mr: parseBoolFlag(r[COL.mr]),
      smi: parseBoolFlag(r[COL.smi]),
      bwsEmail: s(r[COL.bwsEmail]),
      shippedAt: s(r[COL.shippedAt]),
      receivedAt: s(r[COL.receivedAt]),
      contactId: null,
    });
  }
  return out;
}

/** 購入者名 → contactId を解決 (mail_contacts.name 完全一致)。 */
function resolveContactIds(
  rows: ParsedRow[],
  contacts: MailContactRecord[],
): ParsedRow[] {
  const nameMap = new Map<string, string>();
  for (const c of contacts) {
    if (c.name) nameMap.set(c.name.trim(), c.id);
  }
  return rows.map((r) => ({
    ...r,
    contactId: r.purchaserName ? nameMap.get(r.purchaserName) ?? null : null,
  }));
}

export function ImportSoldDevicesDialog({ open, onOpenChange }: Props) {
  const { data: contacts } = useMailContacts();
  const bulkImport = useBulkImportSoldDevices();

  const [step, setStep] = useState<'file' | 'preview'>('file');
  const [parsing, setParsing] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ParsedRow[] | null>(null);
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** 各フィールドの取込 ON/OFF */
  const [enabledFields, setEnabledFields] = useState<Record<FieldKey, boolean>>(
    () => {
      const init = {} as Record<FieldKey, boolean>;
      for (const k of Object.keys(FIELD_LABELS) as FieldKey[]) init[k] = true;
      return init;
    },
  );

  /** 未マッチ購入者名 → アクション */
  const [unmatchedActions, setUnmatchedActions] = useState<
    Record<string, UnmatchedAction>
  >({});

  useEffect(() => {
    if (!open) return;
    setStep('file');
    setFile(null);
    setRows(null);
    setUnmatchedActions({});
  }, [open]);

  // contacts ロード後にリゾルブ
  useEffect(() => {
    if (!rows || !contacts) return;
    setRows((prev) => (prev ? resolveContactIds(prev, contacts) : prev));
    // 一度だけ実行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts?.length, rows?.length]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setParsing(true);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
        header: 1,
        defval: null,
      });
      const parsed = parseSheet(raw as unknown[][]);
      const resolved = contacts ? resolveContactIds(parsed, contacts) : parsed;
      setRows(resolved);
      // 未マッチをデフォルト 'skip' で初期化
      const unmatched = new Set<string>();
      for (const r of resolved) {
        if (!r.contactId && r.purchaserName) unmatched.add(r.purchaserName);
      }
      const init: Record<string, UnmatchedAction> = {};
      for (const n of unmatched) init[n] = 'skip';
      setUnmatchedActions(init);
      toast.success(`${parsed.length} 行を検出`);
      setStep('preview');
    } catch (err) {
      toast.error(`パース失敗: ${(err as Error).message}`);
    } finally {
      setParsing(false);
    }
  };

  /** 種別ごとの件数 */
  const kindSummary = useMemo(() => {
    if (!rows) return [] as { kind: string; count: number }[];
    const m = new Map<string, number>();
    for (const r of rows) m.set(r.productKind, (m.get(r.productKind) ?? 0) + 1);
    return Array.from(m.entries())
      .map(([kind, count]) => ({ kind, count }))
      .sort((a, b) => b.count - a.count);
  }, [rows]);

  const unmatchedNames = useMemo(() => {
    if (!rows) return [] as { name: string; count: number }[];
    const m = new Map<string, number>();
    for (const r of rows) {
      if (!r.contactId && r.purchaserName) {
        m.set(r.purchaserName, (m.get(r.purchaserName) ?? 0) + 1);
      }
    }
    return Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [rows]);

  const matchedCount = useMemo(() => {
    if (!rows) return 0;
    return rows.filter((r) => !!r.contactId).length;
  }, [rows]);

  const willImportCount = useMemo(() => {
    if (!rows) return 0;
    return rows.filter(
      (r) =>
        r.contactId !== null ||
        (r.purchaserName && unmatchedActions[r.purchaserName] === 'create'),
    ).length;
  }, [rows, unmatchedActions]);

  /**
   * スキップ扱いになる未マッチ行を CSV にして自動ダウンロード。
   * 「スキップ」アクションが選択された購入者の全行が対象。
   * 「新規作成」が選択された購入者の行は実際に取り込まれるので含めない。
   */
  const downloadUnmatchedCsv = (rowsToDownload: ParsedRow[]) => {
    if (rowsToDownload.length === 0) return;
    const headers = [
      'legacyDataId',
      'purchaserName',
      'productKind',
      'productName',
      'serialNumber',
      'createdAt',
      'warrantyStart',
      'warrantyEnd',
      'serviceTag',
      'expressCode',
      'nesDeviceKind',
      'mind',
      'mr',
      'smi',
      'bwsEmail',
      'shippedAt',
      'receivedAt',
    ];
    const data = rowsToDownload.map((r) => [
      r.legacyDataId,
      r.purchaserName,
      r.productKind,
      r.productName,
      r.productKind === 'NES Health' ? r.nesSerial : r.twSerial,
      r.createdAt,
      r.warrantyStart,
      r.warrantyEnd,
      r.serviceTag,
      r.expressCode,
      r.nesDeviceKind,
      r.mind == null ? '' : r.mind ? 'true' : 'false',
      r.mr == null ? '' : r.mr ? 'true' : 'false',
      r.smi == null ? '' : r.smi ? 'true' : 'false',
      r.bwsEmail,
      r.shippedAt,
      r.receivedAt,
    ]);
    const csv = Papa.unparse({ fields: headers, data });
    // Excel での文字化け防止のため UTF-8 BOM を付与
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date()
      .toISOString()
      .replace(/[:T]/g, '-')
      .slice(0, 16);
    a.href = url;
    a.download = `unmatched_sold_devices_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!rows) return;

    // 1. 未マッチ顧客で「新規作成」が選ばれているものを mail_contacts に追加
    setCreating(true);
    const nameToNewId = new Map<string, string>();
    try {
      const toCreate = Object.entries(unmatchedActions).filter(
        ([, act]) => act === 'create',
      );
      for (const [name] of toCreate) {
        const ref = doc(collection(db, 'mail_contacts'));
        await setDoc(ref, {
          name,
          emails: [],
          defaultEmail: '',
          customFields: { source_import_note: '稼働機器 Excel 取込で自動作成' },
          tags: ['legacy_import'],
          source: 'manual',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        nameToNewId.set(name, ref.id);
      }
    } catch (err) {
      toast.error(`顧客作成失敗: ${(err as Error).message}`);
      setCreating(false);
      return;
    }
    setCreating(false);

    // 2. インポート対象アイテムを構築
    const items: BulkImportSoldDeviceItem[] = [];
    for (const r of rows) {
      let contactId = r.contactId;
      if (!contactId && r.purchaserName) {
        if (unmatchedActions[r.purchaserName] === 'create') {
          contactId = nameToNewId.get(r.purchaserName) ?? null;
        }
      }
      if (!contactId) continue;

      const isNes = r.productKind === 'NES Health';
      const serial = enabledFields.serialNumber
        ? isNes
          ? r.nesSerial
          : r.twSerial
        : '';

      const item: BulkImportSoldDeviceItem = {
        legacyDataId: r.legacyDataId,
        contactId,
        productKind: r.productKind,
        serialNumber: serial,
        productName: r.productName,
        installedAt: r.createdAt,
        warrantyStartAt: enabledFields.warranty ? r.warrantyStart || null : null,
        warrantyEndAt: enabledFields.warranty ? r.warrantyEnd || null : null,
        serviceTag: enabledFields.serviceTag ? r.serviceTag || null : null,
        expressCode: enabledFields.expressCode ? r.expressCode || null : null,
        remarks: null,
        nesHealth: isNes
          ? {
              deviceKind: enabledFields.nesDeviceKind
                ? r.nesDeviceKind || null
                : null,
              mind: enabledFields.nesMind ? r.mind : null,
              mr: enabledFields.nesMr ? r.mr : null,
              smi: enabledFields.nesSmi ? r.smi : null,
              bwsEmail: enabledFields.nesBwsEmail ? r.bwsEmail || null : null,
              shippedAt: enabledFields.nesShipping ? r.shippedAt || null : null,
              receivedAt: enabledFields.nesShipping
                ? r.receivedAt || null
                : null,
            }
          : null,
      };
      items.push(item);
    }

    if (items.length === 0) {
      toast.error('インポート可能な行がありません');
      return;
    }

    // 3. Callable を chunk で呼び出し (1回 2000 件まで)
    try {
      const CHUNK = 1500;
      let imported = 0;
      let skipped = 0;
      const allErrors: { legacyDataId: string; reason: string }[] = [];
      for (let i = 0; i < items.length; i += CHUNK) {
        const chunk = items.slice(i, i + CHUNK);
        const r = await bulkImport.mutateAsync(chunk);
        imported += r.imported;
        skipped += r.skipped;
        allErrors.push(...r.errors);
      }

      // 4. 「スキップ」扱いの未マッチ行を CSV で自動ダウンロード
      const skippedRows = rows.filter((r) => {
        if (r.contactId) return false; // マッチ済みは取込済み
        if (!r.purchaserName) return true; // 購入者名なしも未マッチ扱い
        return (unmatchedActions[r.purchaserName] ?? 'skip') === 'skip';
      });
      if (skippedRows.length > 0) {
        downloadUnmatchedCsv(skippedRows);
      }

      if (allErrors.length > 0) {
        toast.warning(
          `${imported} 件登録 (${skipped} 件スキップ / エラー ${allErrors.length} 件)${
            skippedRows.length > 0 ? ` / 未マッチ ${skippedRows.length} 行をCSV出力` : ''
          }`,
        );
        console.warn('[ImportSoldDevices] errors:', allErrors);
      } else {
        toast.success(
          `${imported} 件の稼働機器を登録しました${
            skippedRows.length > 0 ? ` (未マッチ ${skippedRows.length} 行をCSV出力)` : ''
          }`,
        );
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(`取込失敗: ${(err as Error).message}`);
    }
  };

  const toggleField = (k: FieldKey) =>
    setEnabledFields((prev) => ({ ...prev, [k]: !prev[k] }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            稼働機器 インポート
            <span className="ml-2 text-xs font-normal text-[var(--text-muted)]">
              ({step === 'file' ? '1/2: ファイル選択' : '2/2: 確認 + 実行'})
            </span>
          </DialogTitle>
          <DialogDescription>
            旧 CRM の「S/N情報」Excel をアップロードして、稼働機器を一括登録します。
            購入者名で既存の顧客に紐付けます。
          </DialogDescription>
        </DialogHeader>

        {step === 'file' && (
          <div className="rounded-md border-2 border-dashed border-[var(--border)] bg-[var(--bg-surface)] p-8 text-center">
            <FileSpreadsheet className="mx-auto mb-3 size-10 text-[var(--text-muted)]" />
            <p className="mb-3 text-sm text-[var(--text-secondary)]">
              {file ? file.name : 'Excel (.xlsx) を選択してください'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={parsing}
            >
              <Upload className="size-3.5" />
              {parsing ? 'パース中…' : 'ファイルを選択'}
            </Button>
            <p className="mt-3 text-[10px] text-[var(--text-muted)]">
              必須列: データID / 種別 / 購入者 / 商品 / 作成日時
            </p>
          </div>
        )}

        {step === 'preview' && rows && (
          <div className="space-y-4">
            {/* サマリー */}
            <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 text-xs">
              <div className="mb-2 font-semibold">サマリー</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span>
                  全行: <strong>{rows.length}</strong>
                </span>
                <span>
                  顧客マッチ:{' '}
                  <strong className="text-[var(--accent-green)]">
                    {matchedCount}
                  </strong>
                </span>
                <span>
                  未マッチ:{' '}
                  <strong
                    className={
                      rows.length - matchedCount > 0
                        ? 'text-[var(--accent-red)]'
                        : ''
                    }
                  >
                    {rows.length - matchedCount}
                  </strong>
                </span>
                <span>
                  → 登録予定:{' '}
                  <strong className="text-[var(--accent)]">
                    {willImportCount}
                  </strong>
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-[var(--text-muted)]">
                {kindSummary.map((k) => (
                  <span key={k.kind}>
                    {k.kind}: <strong>{k.count}</strong>
                  </span>
                ))}
              </div>
            </div>

            {/* フィールド ON/OFF */}
            <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3">
              <div className="mb-2 text-xs font-semibold">取込フィールド</div>
              <div className="grid grid-cols-2 gap-1.5">
                {(Object.keys(FIELD_LABELS) as FieldKey[]).map((k) => (
                  <label
                    key={k}
                    className="flex cursor-pointer items-center gap-2 text-xs"
                  >
                    <input
                      type="checkbox"
                      checked={enabledFields[k]}
                      onChange={() => toggleField(k)}
                    />
                    <span>
                      {FIELD_LABELS[k].label}
                      {FIELD_LABELS[k].scope === 'nesOnly' && (
                        <span className="ml-1 text-[10px] text-[var(--accent-purple)]">
                          [NES Health のみ]
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-[10px] text-[var(--text-muted)]">
                ※ データID / 種別 / 商品 / 作成日時 / 購入者(顧客リンク) は常に取込みます
              </p>
            </div>

            {/* 未マッチ顧客の処理 */}
            {unmatchedNames.length > 0 && (
              <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold">
                    未マッチ購入者 ({unmatchedNames.length} 名)
                  </div>
                  <div className="flex gap-2 text-[10px]">
                    <button
                      type="button"
                      className="text-[var(--accent)] hover:underline"
                      onClick={() => {
                        const next: Record<string, UnmatchedAction> = {};
                        for (const u of unmatchedNames) next[u.name] = 'skip';
                        setUnmatchedActions(next);
                      }}
                    >
                      全てスキップ
                    </button>
                    <button
                      type="button"
                      className="text-[var(--accent)] hover:underline"
                      onClick={() => {
                        const next: Record<string, UnmatchedAction> = {};
                        for (const u of unmatchedNames) next[u.name] = 'create';
                        setUnmatchedActions(next);
                      }}
                    >
                      全て新規作成
                    </button>
                  </div>
                </div>
                <div className="max-h-[200px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {unmatchedNames.map((u) => (
                        <tr
                          key={u.name}
                          className="border-b border-[var(--border)] last:border-b-0"
                        >
                          <td className="px-2 py-1.5">{u.name || '(空)'}</td>
                          <td className="px-2 py-1.5 text-right text-[10px] text-[var(--text-muted)]">
                            {u.count} 行
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <select
                              value={unmatchedActions[u.name] ?? 'skip'}
                              onChange={(e) =>
                                setUnmatchedActions((prev) => ({
                                  ...prev,
                                  [u.name]: e.target.value as UnmatchedAction,
                                }))
                              }
                              className="rounded border border-[var(--border)] bg-[var(--bg-input)] px-2 py-1 text-xs"
                            >
                              <option value="skip">スキップ</option>
                              <option value="create">新規顧客として作成</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* プレビュー (先頭 20 行) */}
            <div className="overflow-hidden rounded-md border border-[var(--border)]">
              <div className="max-h-[280px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 border-b border-[var(--border)] bg-[var(--bg-elevated)]">
                    <tr className="text-left text-[var(--text-muted)] uppercase tracking-wider">
                      <th className="px-2 py-1.5 font-medium">種別</th>
                      <th className="px-2 py-1.5 font-medium">商品</th>
                      <th className="px-2 py-1.5 font-medium">S/N</th>
                      <th className="px-2 py-1.5 font-medium">購入者</th>
                      <th className="px-2 py-1.5 font-medium">作成日</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 20).map((r) => {
                      const isNes = r.productKind === 'NES Health';
                      const sn = isNes ? r.nesSerial : r.twSerial;
                      return (
                        <tr
                          key={r.legacyDataId}
                          className="border-b border-[var(--border)] last:border-b-0"
                        >
                          <td className="px-2 py-1.5">
                            <span className="rounded bg-[var(--bg-hover)] px-1.5 py-0.5 text-[10px]">
                              {r.productKind}
                            </span>
                          </td>
                          <td className="px-2 py-1.5">{r.productName || '-'}</td>
                          <td className="px-2 py-1.5 font-mono text-[10px]">
                            {sn || '-'}
                          </td>
                          <td
                            className={`px-2 py-1.5 ${
                              !r.contactId
                                ? 'text-[var(--accent-red)]'
                                : ''
                            }`}
                          >
                            {r.purchaserName || '-'}
                            {!r.contactId && r.purchaserName && (
                              <span className="ml-1 text-[10px]">✗</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-[10px] text-[var(--text-muted)]">
                            {r.createdAt.slice(0, 10)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {rows.length > 20 && (
                  <div className="border-t border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-1.5 text-center text-[10px] text-[var(--text-muted)]">
                    ... 他 {rows.length - 20} 行
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={bulkImport.isPending || creating}
          >
            キャンセル
          </Button>
          {step === 'preview' && willImportCount > 0 && (
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={bulkImport.isPending || creating}
            >
              {creating
                ? '顧客作成中…'
                : bulkImport.isPending
                  ? '取込中…'
                  : `${willImportCount} 件を取込`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
